import React, { ReactNode } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { CardContainer } from '../Card';
import ColorCheckbox, { ColorName } from '../Checkbox/ColorCheckbox';
import FavoriteCheckbox from '../Checkbox/FavoriteCheckbox';
import breakpoints from '../Theme/breakpoints';

const cardStyles = css`
  flex: 1 2 24%;
  min-width: 280px;
  margin: 0 8px 32px;
`;

const ProductsWrapper = styled.div`
  display: flex;
  justify-content: left;
  flex-direction: column;

  @media (min-width: ${breakpoints.md}px) {
    flex-direction: row;
  }
`;

const ProductsListPanel = styled.div`
  box-sizing: border-box;
  padding: 0 16px;
  width: 100%;

  @media (min-width: ${breakpoints.sm}px) {
    padding: 0 16px 0 8px;
  }
  @media (min-width: ${breakpoints.md}px) {
    width: calc(100% - 322px);
  }
`;

const ProductsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: stretch;

  @media (min-width: ${breakpoints.md}) {
    justify-content: left;
  }

  ::after {
    content: '';
    flex: 1 2 25%;
  }
`;

const ProductItemWrapper = styled.div`
  ${cardStyles}
`;

const ProductPadding = styled.div`
  ${cardStyles}
`;

const ProductsListWrapper = ({ children } : { children: ReactNode }) => (
  <ProductsList>
    {children}
    <ProductPadding />
    <ProductPadding />
  </ProductsList>
);

ProductsListWrapper.propTypes = {
  children: PropTypes.node,
};

const ProductListItemDetailWrapper = styled.div`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.sizes.subtitle1};
  letter-spacing: initial;
  color: ${({ theme }) => theme.colors.greyDark};
  display: flex;
  flex-wrap: wrap;
  padding: 16px 0;
  height: 75px;
`;

const ProductListItemBrand = styled.div`
  && {
    color: ${({ theme }) => theme.colors.black};
    font-weight: bold;
  }
  flex-grow: 1;
`;

const ProductListItemText = styled.div`
  order: 3;
  width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const ProductListItemPrice = styled.div`
  flex-grow: 0;
  order: 2;
`;

const StyledColorCheckbox = styled(ColorCheckbox)`
  &&& {
    cursor: auto;
    margin-right: 0;
  }
`;

const ColorContainer = styled.div`
  display: flex;
  order: 4;
  z-index: 0;
  margin: -6px 0 0 3px;

  .MuiFormControlLabel-label {
    display: none;
  }

  .MuiFormControlLabel-root {
    margin-right: 0;
    padding: 0;
  }

  .MuiCheckbox-root > span {
    width: 10px;
    height: 10px;

    :after {
      top: -5px;
      left: 3px;
      font-size: ${({ theme }) => theme.sizes.overline};
    }
  }

  &:hover {
    .MuiIconButton-colorSecondary {
      background-color: transparent;
    }
  }
`;

const productListItemDetailPropertyTypes = {
  productName: PropTypes.string.isRequired,
  brandName: PropTypes.string.isRequired,
  price: PropTypes.string,
  season: PropTypes.string,
  colors: PropTypes.arrayOf(
    PropTypes.string,
  ),
};

type ProductListItemDetailProperties = Omit<InferProps<
  typeof productListItemDetailPropertyTypes
>, 'colors'> & {
  colors?: ColorName[];
};

const ProductListItemDetail = ({
  productName, brandName, price, season, colors,
}: ProductListItemDetailProperties) => (
  <ProductListItemDetailWrapper>
    <ProductListItemBrand>{brandName}</ProductListItemBrand>
    <ProductListItemText>{productName}</ProductListItemText>
    <ProductListItemPrice>{price}</ProductListItemPrice>
    <ProductListItemText>{season}</ProductListItemText>
    {colors?.map((color) => (
      <ColorContainer key={color}>
        <StyledColorCheckbox name={color} label={color} />
      </ColorContainer>
    ))}
  </ProductListItemDetailWrapper>
);

ProductListItemDetail.propTypes = productListItemDetailPropertyTypes;

const ProductCardContainer = styled(CardContainer)`
  height: 100%;
  display: flex;
  justify-content: space-between;
  position: relative;
  z-index: 0;
`;

const ProductImageContainer = styled.div`
  align-items: start;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  flex: 1 1 100%;
`;

const ProductListItemFavorite = styled.div`
  top: 8px;
  right: -8px;
  position: absolute;
  z-index: 1;
`;

const productCardPropertyTypes = {
  children: PropTypes.node,
  favorited: PropTypes.bool,
  onFavoriteClick: PropTypes.func.isRequired,
  productImage: PropTypes.node,
};

type ProductCardProperties = InferProps<typeof productCardPropertyTypes> & {
  favorited?: boolean;
};

const ProductCard = ({
  productImage,
  children,
  favorited = false,
  onFavoriteClick,
}: ProductCardProperties) => (
  <ProductCardContainer className="card-container">
    {productImage && (
      <ProductImageContainer>
        <ProductListItemFavorite>
          <FavoriteCheckbox
            onClick={onFavoriteClick}
            checked={favorited}
          />
        </ProductListItemFavorite>
        {productImage}
      </ProductImageContainer>
    )}
    {children}
  </ProductCardContainer>
);

export {
  ProductsWrapper,
  ProductsList,
  ProductsListWrapper,
  ProductItemWrapper,
  ProductsListPanel,
  ProductListItemDetail,
  ProductCard,
};
